import { useI18n } from '../../i18n'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type SeverityKey = 'critical' | 'high' | 'medium' | 'low' | 'info'

type Props = {
  reportName: string
  lastScanLabel: string
  assetsWithVulns: number
  linkedSystems: number
  severity: Record<SeverityKey, number>
  securityHealth: number
  riskScore: number
  scanner?: string | null
  unregisteredScannerTargets?: {
    scopeLabel?: string
    assetTargets?: number
    uniqueFindings?: number
    affectedInstances?: number
    severityDistribution?: Partial<Record<SeverityKey, number>>
    scannerSources?: string[]
  } | null
}

function healthColor(percent: number): string {
  if (percent >= 85) return '#34d399'
  if (percent >= 40) return '#fbbf24'
  return '#f87171'
}

export default function ReportSummaryPosturePanel({
  reportName,
  lastScanLabel,
  assetsWithVulns,
  linkedSystems,
  severity,
  securityHealth,
  riskScore,
  scanner,
  unregisteredScannerTargets = null,
}: Props) {
  const { t } = useI18n()
  const pct = Math.max(0, Math.min(100, Math.round(securityHealth)))
  const accent = healthColor(pct)

  return (
    <section className={entStyles.layer} data-testid="report-summary-posture">
      <p className={entStyles.layerLabel}>
        {t('reports.summary.layerLabel', 'Selected report posture')}
      </p>
      <div className={entStyles.posturePanel}>
        <h2 className={entStyles.postureTitle}>{reportName}</h2>
        <p className={entStyles.postureSubtitle}>
          {t('reports.lastScan', 'Last scan')}: {lastScanLabel}
        </p>

        <div className={entStyles.postureGrid}>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.summary.postureScore', 'Security posture')}
            </p>
            <p className={entStyles.postureMetricValue} style={{ color: accent }}>
              {pct}%
            </p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.riskScore', 'Risk score')}</p>
            <p className={entStyles.postureMetricValue}>{riskScore}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.metrics.assetsWithVulns', 'Assets with vulnerabilities')}
            </p>
            <p className={entStyles.postureMetricValue}>{assetsWithVulns}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.metrics.informationSystems', 'Information systems')}
            </p>
            <p className={entStyles.postureMetricValue}>{linkedSystems}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('severity.critical', 'Critical')}</p>
            <p className={entStyles.postureMetricValue}>{severity.critical ?? 0}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('severity.high', 'High')}</p>
            <p className={entStyles.postureMetricValue}>{severity.high ?? 0}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('severity.medium', 'Medium')}</p>
            <p className={entStyles.postureMetricValue}>{severity.medium ?? 0}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('severity.low', 'Low')}</p>
            <p className={entStyles.postureMetricValue}>{severity.low ?? 0}</p>
          </div>
        </div>

        {scanner ? (
          <p className={entStyles.postureStory}>
            {t('reports.scannerLabel', 'Scanner')}: {scanner}
          </p>
        ) : null}
        {unregisteredScannerTargets &&
        (Number(unregisteredScannerTargets.uniqueFindings || 0) > 0 ||
          Number(unregisteredScannerTargets.assetTargets || 0) > 0) ? (
          <p className={entStyles.scopeLine} data-testid="unregistered-scanner-targets">
            {unregisteredScannerTargets.scopeLabel ||
              t(
                'reports.unregisteredScannerTargets',
                'Unregistered scanner targets (current scan results, not registered inventory)',
              )}
            {': '}
            {Number(unregisteredScannerTargets.assetTargets || 0)}{' '}
            {t('reports.unregisteredTargets', 'targets')}
            {' · '}
            {Number(unregisteredScannerTargets.uniqueFindings || 0)}{' '}
            {t('reports.uniqueFindings', 'unique findings')}
            {' · '}
            {t('labels.severityMedium', 'Medium')}{' '}
            {Number(unregisteredScannerTargets.severityDistribution?.medium || 0)}
            {' · '}
            {t('labels.severityLow', 'Low')}{' '}
            {Number(unregisteredScannerTargets.severityDistribution?.low || 0)}
            {unregisteredScannerTargets.scannerSources?.length
              ? ` · ${unregisteredScannerTargets.scannerSources.join(' · ')}`
              : ''}
          </p>
        ) : null}
      </div>
    </section>
  )
}
